import { Form, Modal, Select, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { CountingResponseData } from '@/types/api/chargingPile/getChargingPileExpenseList';
import type { ChargingPileElectricityRate } from '@/types/database/chargingPile/chargingPileElectricityRate';

import styles from './index.module.scss';
import { countingColumns } from './tableColumnsSetting';

const { Item } = Form;
const { confirm } = Modal;

const getFormItemTitles = () => {
  const titles = countingColumns.map(
    (column: { title: string; dataIndex: string }) => ({
      title: column.title,
      dataIndex: column.dataIndex,
    })
  );

  return titles;
};

const pricingStandardOptions = [
  {
    key: 'summerPeak',
    label: '夏季尖峰',
    value: '夏季尖峰',
  },
  {
    key: 'summerOffPeak',
    label: '夏季離峰',
    value: '夏季離峰',
  },
  {
    key: 'nonSummerPeak',
    label: '非夏季尖峰',
    value: '非夏季尖峰',
  },
  {
    key: 'nonSummerOffPeak',
    label: '非夏季離峰',
    value: '非夏季離峰',
  },
];

export const getPricingUnitPrice = (
  pricingStandard: '夏季尖峰' | '夏季離峰' | '非夏季尖峰' | '非夏季離峰',
  chargingPileElectricityRate: ChargingPileElectricityRate[]
) => {
  const { summer, nonSummer } = chargingPileElectricityRate[0];

  switch (pricingStandard) {
    case '夏季尖峰':
      return summer.peak.price;

    case '夏季離峰':
      return summer.offPeak.price;

    case '非夏季尖峰':
      return nonSummer.peak.price;

    default:
      return nonSummer.offPeak.price;
  }
};

export const getExpense = (pricingUnitPrice: number, usage: number) => {
  const newExpense = (pricingUnitPrice * usage).toFixed(2);
  return newExpense;
};

export const getFormItems = () => {
  const titles = getFormItemTitles();

  const formItems = titles.map((titleObj) => {
    return (
      <Item
        key={titleObj.dataIndex}
        label={titleObj.title}
        name={titleObj.dataIndex}
        valuePropName={
          titleObj.dataIndex === 'pricingStandard' ? 'value' : 'children'
        }
        required
      >
        {titleObj.dataIndex === 'pricingStandard' ? (
          <Select
            options={pricingStandardOptions}
            placeholder="請選擇計價標準"
          />
        ) : (
          <p className={styles.itemContent} />
        )}
      </Item>
    );
  });

  return formItems;
};

const changeChargingPileRate = async (args: {
  formValues: CountingResponseData;
  closeEditModal: () => void;
  handleIsNeedUpdatePage: (isNeedUpdatePage: boolean) => void;
}) => {
  const { formValues, closeEditModal, handleIsNeedUpdatePage } = args;

  const {
    startTime,
    endTime,
    chargingPileID,
    pricingStandard,
    pricingUnitPrice,
  } = formValues;

  const timeStart = dayjs(startTime).valueOf();
  const timeEnd = dayjs(endTime).valueOf();

  try {
    await axios.patch(
      '/ezcon/api/historyExpense',
      {
        timeStart,
        timeEnd,
        chargingPileID,
        modifiedChineseCommunityElectricityRate: pricingStandard,
        modifiedCommunityElectricityRate: pricingUnitPrice,
      },
      {
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
      }
    );
    closeEditModal();
    handleIsNeedUpdatePage(true);
    message.success('計價標準更改成功!');
  } catch (error) {
    console.error(error);
    message.error('計價標準更改失敗!');
  }
};

export const confirmChangePricingStandard = (args: {
  formValues: CountingResponseData;
  closeEditModal: () => void;
  handleIsNeedUpdatePage: (isNeedUpdatePage: boolean) => void;
}) => {
  confirm({
    title: '確定要更改計價標準？',
    icon: <ExclamationCircleOutlined />,
    content: '請注意，按下確定鍵後，會立刻修改此次的計費方式！',
    onOk() {
      changeChargingPileRate(args);
    },
  });
};

export const checkIsSuperAdmin = (auth: string) => {
  const isSuperAdmin = auth === 'superAdmin';
  return isSuperAdmin;
};
